"use client";

import * as React from "react";
import { useStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  BookOpen,
  Upload,
  ClipboardPaste,
  FileText,
  Sparkles,
  Loader2,
  X,
  BookMarked,
} from "lucide-react";
import { cn } from "@/lib/utils";

const SAMPLE_TEXTS: { title: string; preview: string; text: string }[] = [
  {
    title: "The Gift of the Magi (excerpt)",
    preview:
      "One dollar and eighty-seven cents. That was all. And sixty cents of it was in pennies...",
    text: `One dollar and eighty-seven cents. That was all. And sixty cents of it was in pennies. Pennies saved one and two at a time by bulldozing the grocer and the vegetable man and the butcher until one's cheeks burned with the silent imputation of parsimony that such close dealing implied. Three times Della counted it. One dollar and eighty-seven cents. And the next day would be Christmas.

There was clearly nothing to do but flop down on the shabby little couch and howl. So Della did it. Which instigates the moral reflection that life is made up of sobs, sniffles, and smiles, with sniffles predominating.

While the mistress of the home is gradually subsiding from the first stage to the second, take a look at the home. A furnished flat at eight dollars per week. It did not exactly beggar description, but it certainly had that word on the lookout for the mendicancy squad.

In the vestibule below was a letter-box into which no letter would go, and an electric button from which no mortal finger could coax a ring. Also appertaining thereunto was a card bearing the name "Mr. James Dillingham Young."`,
  },
  {
    title: "A Short Tale About the Sea",
    preview: "The old fisherman had seen many storms in his life...",
    text: `The old fisherman had seen many storms in his life, but none quite like the one that gathered on the horizon that morning. The sky turned the color of bruised plums, and the gulls fled inland, crying out their warnings to anyone who would listen.

He checked his nets one last time. The rope was frayed in places, but it would hold. It always had. His father had taught him that the sea gives and the sea takes, and a wise man knows the difference.

He pushed the small boat into the surf and jumped in, his bones aching from the cold. The waves slapped against the hull like impatient hands. He smiled, because the sea was honest, even when it was cruel. You always knew where you stood with the sea, even when you could not stand at all.`,
  },
];

export function HomeScreen() {
  const loadText = useStore((s) => s.loadText);
  const vocabulary = useStore((s) => s.vocabulary);
  const { toast } = useToast();

  const [tab, setTab] = React.useState<"paste" | "upload">("paste");
  const [pasted, setPasted] = React.useState("");
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [extractedText, setExtractedText] = React.useState<string>("");
  const [extractedTitle, setExtractedTitle] = React.useState<string>("");
  const [extracting, setExtracting] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const wordCount = React.useMemo(() => {
    const t = tab === "paste" ? pasted : extractedText;
    if (!t.trim()) return 0;
    return (t.match(/[A-Za-z0-9]+(?:['-][A-Za-z0-9]+)*/g) || []).length;
  }, [pasted, extractedText, tab]);

  const charCount = React.useMemo(() => {
    const t = tab === "paste" ? pasted : extractedText;
    return t.length;
  }, [pasted, extractedText, tab]);

  async function handleFile(file: File) {
    setFileError(null);
    setFileName(file.name);
    setExtractedText("");
    setExtractedTitle("");
    setExtracting(true);
    setProgress(10);

    try {
      const formData = new FormData();
      formData.append("file", file);

      setProgress(30);
      const res = await fetch("/api/extract-text", { method: "POST", body: formData });
      setProgress(80);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Extraction failed (HTTP ${res.status})`);
      }

      const data = (await res.json()) as { text: string; title: string; chars: number };
      setExtractedText(data.text);
      setExtractedTitle(data.title);
      setProgress(100);
      toast({
        title: "Texto extraído!",
        description: `${data.chars.toLocaleString()} caracteres • ${file.name}`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Falha ao extrair texto.";
      setFileError(msg);
      setFileName(null);
      toast({ title: "Erro na extração", description: msg, variant: "destructive" });
    } finally {
      setExtracting(false);
      setTimeout(() => setProgress(0), 600);
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  }

  function startReading() {
    const text = tab === "paste" ? pasted : extractedText;
    const title = tab === "paste" ? "" : extractedTitle;
    if (!text.trim()) {
      toast({
        title: "Nada para ler ainda",
        description: "Cole um texto ou faça upload de um PDF/TXT primeiro.",
        variant: "destructive",
      });
      return;
    }
    if (wordCount < 5) {
      toast({
        title: "Texto muito curto",
        description: "Cole um texto com pelo menos algumas frases para uma boa experiência.",
        variant: "destructive",
      });
      return;
    }
    loadText(text, title);
  }

  function loadSample(sample: (typeof SAMPLE_TEXTS)[number]) {
    setTab("paste");
    setPasted(sample.text);
    toast({ title: "Exemplo carregado", description: sample.title });
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <BookOpen className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">ReadFluent</h1>
              <p className="text-xs text-muted-foreground">
                Aprenda inglês lendo textos que você escolhe
              </p>
            </div>
          </div>
          {vocabulary.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <BookMarked className="h-4 w-4" />
              <span>{vocabulary.length} palavra(s) no vocabulário</span>
            </div>
          )}
        </div>
      </header>

      {/* Hero + input */}
      <main className="flex-1 mx-auto max-w-6xl w-full px-6 py-10 rf-fade-in">
        <section className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border bg-muted/40 px-3 py-1 text-xs text-muted-foreground mb-4">
            <Sparkles className="h-3.5 w-3.5" />
            Otimizado para uso no PC • Use o teclado para tudo
          </div>
          <h2 className="text-4xl font-bold tracking-tight mb-3">
            Cole um texto. <span className="text-muted-foreground">Leia como num Kindle.</span>
            <br />
            Aprenda inglês palavra por palavra.
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Cole qualquer texto em inglês ou faça upload de um PDF/TXT. Clique em uma palavra para
            ver tradução, definição e pronúncia. Navegue entre palavras, frases e parágrafos com o
            teclado.
          </p>
        </section>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "paste" | "upload")} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-6">
            <TabsTrigger value="paste" className="gap-2">
              <ClipboardPaste className="h-4 w-4" />
              Colar texto
            </TabsTrigger>
            <TabsTrigger value="upload" className="gap-2">
              <Upload className="h-4 w-4" />
              Enviar arquivo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="paste" className="rf-fade-in">
            <Card className="p-6">
              <label className="text-sm font-medium mb-2 block">
                Cole aqui o texto em inglês
              </label>
              <Textarea
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                placeholder="Paste any English text here — an article, a chapter of a book, a short story, news, anything you want to read..."
                className="min-h-[320px] resize-y font-mono text-sm leading-relaxed"
              />
              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {wordCount.toLocaleString()} palavras • {charCount.toLocaleString()} caracteres
                </span>
                {pasted && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPasted("")}
                    className="h-7 text-xs"
                  >
                    <X className="h-3 w-3 mr-1" /> Limpar
                  </Button>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="upload" className="rf-fade-in">
            <Card className="p-6">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,.md,text/plain,application/pdf"
                onChange={onFileChange}
                className="hidden"
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={cn(
                  "border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors",
                  dragging
                    ? "border-primary bg-primary/5"
                    : "border-muted-foreground/30 hover:border-foreground/40 hover:bg-muted/30"
                )}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                    {extracting ? (
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    ) : (
                      <Upload className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">
                      {extracting
                        ? "Extraindo texto..."
                        : fileName
                        ? "Trocar arquivo"
                        : "Arraste um arquivo ou clique para escolher"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Suporta PDF (.pdf) e texto (.txt, .md)
                    </p>
                  </div>
                </div>
              </div>

              {progress > 0 && progress < 100 && (
                <div className="mt-4">
                  <Progress value={progress} className="h-1" />
                </div>
              )}

              {fileError && (
                <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  {fileError}
                </div>
              )}

              {fileName && extractedText && !extracting && (
                <div className="mt-4 rounded-md border bg-muted/30 p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="font-medium truncate">{fileName}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFileName(null);
                        setExtractedText("");
                        setExtractedTitle("");
                      }}
                      className="h-7 text-xs shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground mb-3">
                    {wordCount.toLocaleString()} palavras • {charCount.toLocaleString()} caracteres
                  </div>
                  <div className="max-h-32 overflow-y-auto reader-scroll text-xs text-muted-foreground bg-background/60 rounded p-3 border">
                    {extractedText.slice(0, 800)}
                    {extractedText.length > 800 ? "…" : ""}
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* Start button */}
        <div className="flex justify-center mt-8">
          <Button
            size="lg"
            onClick={startReading}
            disabled={
              tab === "paste" ? !pasted.trim() : !extractedText || extracting
            }
            className="min-w-[200px]"
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Começar a ler
          </Button>
        </div>

        {/* Samples */}
        <section className="mt-14">
          <h3 className="text-sm font-medium text-muted-foreground mb-3 text-center">
            Ou comece com um exemplo:
          </h3>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
            {SAMPLE_TEXTS.map((s) => (
              <button
                key={s.title}
                onClick={() => loadSample(s)}
                className="text-left rounded-lg border bg-card p-4 hover:border-foreground/30 hover:bg-muted/30 transition-colors"
              >
                <div className="font-medium text-sm mb-1">{s.title}</div>
                <p className="text-xs text-muted-foreground line-clamp-2">{s.preview}</p>
              </button>
            ))}
          </div>
        </section>
      </main>

      <footer className="mt-auto border-t bg-background/60">
        <div className="mx-auto max-w-6xl px-6 py-4 text-xs text-muted-foreground text-center">
          ReadFluent — leia em inglês, clique nas palavras, aprenda no seu ritmo.
        </div>
      </footer>
    </div>
  );
}
